import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import CustomMUITable from "src/components/GeneralComponents/CustomMUITable/CustomMUITable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { s3baseUrl } from "src/config/config";
import {
  memberCancleSubscribergApi,
  memberSubscriberListingApi,
} from "src/DAL/member/member";
import moment from "moment";
import Label from "src/components/Label";
import { show_proper_words } from "src/utils/constant";

const TABLE_HEAD = [
  {
    id: "action",
    label: "Action",
    alignRight: false,
    MENU_OPTIONS: "MENU_OPTIONS",
    type: "action",
  },
  { id: "number", label: "#", alignRight: false, type: "number" },
  { id: "page_name", label: "Page Title", alignRight: false },
  { id: "plan_title", label: "Plan Title", alignRight: false },
  {
    id: "created_at",
    label: "Subscription Date",
    alignRight: false,
  },
  {
    id: "nextPaymentDate",
    label: "Next Payment Date",
    alignRight: false,
  },
  {
    id: "statusDate",
    label: "Status",
    alignRight: false,
    renderData: (row) => {
      return (
        <div style={{ display: "flex" }}>
          <Label
            variant="ghost"
            color={
              row.statusDate.includes("Cancels")
                ? "warning"
                : row.statusDate.includes("Trial")
                ? "info"
                : row.statusDate === "Expired"
                ? "error"
                : "success"
            }
          >
            {row.statusDate.includes("Cancels") && (
              <AccessTimeIcon
                style={{ marginRight: "5px", width: "18px", height: "18px" }}
              />
            )}

            {row.statusDate}
          </Label>
          {row?.reason && row.statusDate != "Completed" && (
            <Tooltip title={row?.reason}>
              <InfoOutlinedIcon
                style={{
                  marginLeft: "5px",
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    },
  },
];

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export default function MemberSubscriptions() {
  const navigate = useNavigate();
  const { member_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [openDelete, setOpenDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionsData, setSubscriptionsData] = useState([]);
  const classes = useStyles();
  const [deleteDoc, setDeleteDoc] = useState("");

  const getMembersList = async () => {
    setIsLoading(true);
    const result = await memberSubscriberListingApi(member_id);
    if (result?.code == 200) {
      const members = result?.subscription?.map((event, index) => {
        let event_plan = event?.plan;
        let plan_title = "N/A";
        let created_at = "N/A";
        let affiliate_name = "N/A";
        if (event_plan?.plan_title) {
          plan_title = event_plan?.plan_title;
        }
        let payment_access = event_plan?.payment_access;
        if (plan_title != "N/A") {
          if (event_plan?.is_plan_free) {
            plan_title = plan_title + " (Free)";
          } else if (payment_access == "recursion") {
            plan_title = plan_title + " (Recurring)";
          } else {
            plan_title = plan_title;
          }
        }
        let affiliate = event?.affiliate?.affiliate_user_info;
        if (affiliate && affiliate.first_name) {
          affiliate_name = affiliate.first_name + " " + affiliate.last_name;
        }

        if (event?.createdAt) {
          created_at = moment(event?.createdAt).format("DD-MM-YYYY");
        }
        let statusTab = "Active";
        if (event?.stripe_subscription_status) {
          statusTab = show_proper_words(event?.stripe_subscription_status);
        } else if (event?.is_completed == true) {
          statusTab = "Completed";
        } else if (event?.cancelation_requested == true) {
          let date = moment(event.subscription_cancelled_date).format(
            "DD MMMM YYYY",
          );
          statusTab = "Cancels at: " + date;
        } else if (event?.is_on_trial == true) {
          let date = moment(event?.trial_ending_date).format("DD MMMM YYYY");
          statusTab = "Trial Ends at: " + date;
        }
        let nextDate = "N/A";
        if (event?.is_completed == true) {
          nextDate = "N/A";
        } else if (event?.is_on_trial == true) {
          let date = moment(event?.trial_ending_date).format("DD MMMM YYYY");
          nextDate = date;
        } else if (event?.next_payment_date) {
          let date = moment(event?.next_payment_date).format("DD-MM-YYYY");
          nextDate = date;
        }
        return {
          ...event,
          page_name: event?.sale_page?.sale_page_title,
          created_at,
          nextPaymentDate: nextDate,
          plan_title: plan_title,
          affiliate_name: affiliate_name,
          MENU_OPTIONS:
            event?.cancelation_requested == true ? (
              <>
                <AccessTimeIcon />
              </>
            ) : (
              menuHandling(event)
            ),
          statusDate:
            event?.is_completed == true
              ? statusTab
              : event?.cancelation_requested == true
              ? statusTab
              : event?.is_on_trial === true
              ? statusTab
              : event.subscription_status === false
              ? "Expired"
              : "Active",
          preview_link_data: {
            to: s3baseUrl + event?.aggrement_pdf_url,
            target: "_blank",
            show_text: event?.aggrement_pdf_url ? "Preview" : "",
            className: "anchor-style",
          },
        };
      });
      setSubscriptionsData(members);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result?.message, {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleAgreeDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    setIsLoading(true);
    const result = await memberCancleSubscribergApi(member_id, deleteDoc._id);
    if (result?.code === 200) {
      getMembersList();
      enqueueSnackbar(result?.message, { variant: "success" });
    } else {
      setIsLoading(false);
      enqueueSnackbar(result?.message, { variant: "error" });
    }
  };

  const menuHandling = (value) => {
    const MENU_OPTIONS = [];

    if (value?.is_completed) {
    } else {
      if (
        value?.cancelation_requested == false &&
        value?.subscription_status == true
      ) {
        MENU_OPTIONS.push({
          label: "Cancel",
          icon: "ant-design:delete-twotone",
          handleClick: handleAgreeDelete,
        });
      }
    }

    return MENU_OPTIONS;
  };

  useEffect(() => {
    getMembersList();
  }, []);

  if (isLoading == true) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to cancel this subscription?"}
        handleAgree={handleDelete}
      />
      <div className="container">
        <div className="row">
          <div className="col-12 d-flex">
            <IconButton
              className="back-screen-button"
              onClick={() => navigate(-1)}
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div className="col-lg-8 col-sm-12 mb-3 mt-3 d-flex align-items-center">
            <h2>Member Subscriptions</h2>
          </div>
          <div className="col-lg-4 col-sm-12 text-end"></div>
        </div>
        <div className="row">
          <div className="col-12">
            <CustomMUITable
              TABLE_HEAD={TABLE_HEAD}
              data={subscriptionsData}
              className="card-with-background"
            />
          </div>
        </div>
      </div>
    </>
  );
}
